import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaClock, FaExclamationTriangle, FaFileCsv, FaSearch, FaShoppingCart, FaTicketAlt, FaUsers } from 'react-icons/fa';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import useFormattingUtils from '../../../../context/useFormattingUtils';
import { useToastHandler } from '../../../common/ToastHandler';
import './StaffSalesActivity.css';

const csvValue = value => `"${String(value ?? '').replaceAll('"', '""')}"`;

const toLocalDateStr = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Monday-start week helper: the Date for Monday of the week `d` falls in.
const startOfWeek = (d) => {
  const copy = new Date(d);
  const day = copy.getDay(); // 0 = Sunday ... 6 = Saturday
  const diffToMonday = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diffToMonday);
  return copy;
};

const DATE_PRESETS = [
  {
    key: 'this_month',
    label: 'This Month',
    range: () => {
      const now = new Date();
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    },
  },
  {
    key: 'last_month',
    label: 'Last Month',
    range: () => {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0),
      };
    },
  },
  {
    key: 'this_week',
    label: 'This Week',
    range: () => {
      const now = new Date();
      return { start: startOfWeek(now), end: now };
    },
  },
  {
    key: 'last_week',
    label: 'Last Week',
    range: () => {
      const now = new Date();
      const thisMonday = startOfWeek(now);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(lastSunday.getDate() - 1);
      return { start: lastMonday, end: lastSunday };
    },
  },
];

const FALLBACK_APPLICATIONS = [
  { key: 'pos', label: 'Quick Invoice' },
  { key: 'retail', label: 'Retail Invoice' },
  { key: 'wholesale', label: 'Wholesale Invoice' },
  { key: 'lottery', label: 'Lottery' },
];

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '—';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs <= 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
};

export default function StaffSalesActivity() {
  const { user } = useContext(AuthContext);
  const { Get, getAPI } = useApi();
  const { formatCurrency } = useFormattingUtils();
  const { handleToast } = useToastHandler();
  const [filters, setFilters] = useState({ date_from: '', date_to: '', employee_id: '', application: 'all', shift_status: 'all' });
  const [activePreset, setActivePreset] = useState(null);
  const [data, setData] = useState({ results: [], summary: {}, employees: [], applications: [], count: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (!user?.store) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ store: user.store, page, page_size: 25 });
        Object.entries(filters).forEach(([key, value]) => value && value !== 'all' && params.set(key, value));
        const response = await Get(`${getAPI('staffSalesActivity')}?${params}`);
        if (active) setData(response || { results: [], summary: {}, employees: [], applications: [], count: 0 });
      } catch (error) {
        handleToast({ type: 'ERROR', message: error.response?.data?.detail || 'Unable to load staff sales activity.' });
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [user?.store, filters, page, Get, getAPI, handleToast]);

  const applyPreset = (preset) => {
    const { start, end } = preset.range();
    setActivePreset(preset.key);
    setFilters(current => ({ ...current, date_from: toLocalDateStr(start), date_to: toLocalDateStr(end) }));
    setPage(1);
  };

  const change = event => {
    setActivePreset(null);
    setFilters(current => ({ ...current, [event.target.name]: event.target.value }));
    setPage(1);
  };

  const applicationOptions = data.applications?.length ? data.applications : FALLBACK_APPLICATIONS;

  const rows = useMemo(
    () => data.results.filter(row => !search || [row.employee_name, row.item_name, row.transaction_id].some(value => String(value || '').toLowerCase().includes(search.toLowerCase()))),
    [data.results, search]
  );

  // Group individual sale lines into one row per invoice/transaction so the
  // table shows "one sale, N products" instead of a line per product. Rows
  // with no transaction_id (e.g. some lottery entries) stay standalone.
  const groups = useMemo(() => {
    const byKey = new Map();
    const order = [];
    rows.forEach(row => {
      const key = row.transaction_id ? `${row.source}-${row.transaction_id}` : `single-${row.id}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          occurred_at: row.occurred_at,
          employee_name: row.employee_name,
          role: row.role,
          employee_email: row.employee_email,
          source: row.source,
          invoice_type: row.invoice_type,
          application: row.application,
          application_label: row.application_label,
          transaction_id: row.transaction_id,
          had_active_shift: row.had_active_shift,
          shift_id: row.shift_id,
          shift_duration_minutes: row.shift_duration_minutes,
          items: [],
          total_amount: 0,
          total_qty: 0,
        });
        order.push(key);
      }
      const group = byKey.get(key);
      group.items.push(row);
      group.total_amount += Number(row.amount || 0);
      group.total_qty += Number(row.quantity || 0);
      if (row.occurred_at && (!group.occurred_at || new Date(row.occurred_at) > new Date(group.occurred_at))) {
        group.occurred_at = row.occurred_at;
        group.shift_duration_minutes = row.shift_duration_minutes;
      }
    });
    return order.map(key => byKey.get(key));
  }, [rows]);

  const pages = Math.max(1, Math.ceil(Number(data.count || 0) / 25));

  const toggleExpanded = (key) => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const exportCsv = () => {
    const output = [['Staff', 'Role', 'Application', 'Invoice / group', 'Product / game', 'Quantity', 'Amount', 'Shift', 'In shift', 'Time'], ...rows.map(row => [row.employee_name, row.role, row.application_label || row.source, row.transaction_id, row.item_name, row.quantity, row.amount, row.had_active_shift ? 'Active' : 'No active shift', formatDuration(row.shift_duration_minutes), row.occurred_at])];
    const url = URL.createObjectURL(new Blob([output.map(line => line.map(csvValue).join(',')).join('\n')], { type: 'text/csv' }));
    const link = document.createElement('a'); link.href = url; link.download = 'staff-sales-activity.csv'; link.click(); URL.revokeObjectURL(url);
  };

  return <main className="staff-sales-activity">
    <header><div><span><FaUsers /> STAFF ACCOUNTABILITY</span><h1>Staff Sales Activity</h1><p>Track product and lottery sales by employee, manager or owner, including sales recorded outside an active shift.</p></div><button onClick={exportCsv}><FaFileCsv /> Export CSV</button></header>
    <section className="staff-sales-stats">
      <article><FaShoppingCart /><div><strong>{data.summary?.product_sales || 0}</strong><span>Product sale lines</span></div></article>
      <article><FaTicketAlt /><div><strong>{data.summary?.lottery_sales || 0}</strong><span>Lottery tickets</span></div></article>
      <article><FaClock /><div><strong>{formatCurrency(data.summary?.total_amount)}</strong><span>Tracked value</span></div></article>
      <article className="warning"><FaExclamationTriangle /><div><strong>{data.summary?.without_shift || 0}</strong><span>Without active shift</span></div></article>
    </section>
    <section className="staff-sales-presets">
      {DATE_PRESETS.map(preset => (
        <button
          key={preset.key}
          type="button"
          className={activePreset === preset.key ? 'active' : ''}
          onClick={() => applyPreset(preset)}
        >
          {preset.label}
        </button>
      ))}
      <span className="staff-sales-presets-divider" />
      <label className="activity-search"><FaSearch /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Employee, product or invoice" /></label>
    </section>
    <section className="staff-sales-filters">
      <label>From<input type="date" name="date_from" value={filters.date_from} onChange={change} /></label>
      <label>To<input type="date" name="date_to" value={filters.date_to} onChange={change} /></label>
      <label>Staff<select name="employee_id" value={filters.employee_id} onChange={change}><option value="">All staff</option>{data.employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</select></label>
      <label>Application<select name="application" value={filters.application} onChange={change}><option value="all">All applications</option>{applicationOptions.map(app => <option key={app.key} value={app.key}>{app.label}</option>)}</select></label>
      <label>Shift<select name="shift_status" value={filters.shift_status} onChange={change}><option value="all">All shift statuses</option><option value="active">Active shift only</option><option value="missing">Without shift only</option></select></label>
    </section>
    <section className="staff-sales-table">
      <table>
        <thead><tr><th></th><th>Staff</th><th>Application</th><th>Transaction</th><th>Items</th><th>Qty</th><th>Amount</th><th>Shift</th><th>In Shift</th><th>Date &amp; Time</th></tr></thead>
        <tbody>
          {groups.map(group => {
            const isExpanded = expanded.has(group.key);
            const isMultiItem = group.items.length > 1;
            return <React.Fragment key={group.key}>
              <tr
                className={`activity-group-row ${isMultiItem ? 'expandable' : ''}`}
                onClick={() => isMultiItem && toggleExpanded(group.key)}
              >
                <td className="activity-expand-cell">
                  {isMultiItem && <FaChevronDown className={`activity-chevron ${isExpanded ? 'open' : ''}`} />}
                </td>
                <td><strong>{group.employee_name}</strong><small>{group.role || group.employee_email || '—'}</small></td>
                <td><span className={`source ${group.source}`}>{group.application_label || group.invoice_type || 'Sales'}</span></td>
                <td>{group.transaction_id || '—'}</td>
                <td>{isMultiItem ? `${group.items.length} products` : group.items[0]?.item_name}</td>
                <td>{group.total_qty}</td>
                <td><b>{formatCurrency(group.total_amount)}</b></td>
                <td>{group.had_active_shift ? <span className="shift active">Active</span> : <span className="shift missing">No active shift</span>}</td>
                <td>{group.had_active_shift ? formatDuration(group.shift_duration_minutes) : '—'}</td>
                <td>{group.occurred_at ? new Date(group.occurred_at).toLocaleString() : '—'}</td>
              </tr>
              {isMultiItem && isExpanded && group.items.map(item => (
                <tr className="activity-item-row" key={item.id}>
                  <td></td>
                  <td colSpan={5} className="activity-item-name">{item.item_name} <span className="activity-item-qty">× {item.quantity}</span></td>
                  <td>{formatCurrency(item.amount)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              ))}
            </React.Fragment>;
          })}
        </tbody>
      </table>
      {loading && <p>Loading staff activity…</p>}
      {!loading && !groups.length && <p>No sales match these filters.</p>}
    </section>
    {pages > 1 && <nav><button disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</button><span>Page {page} of {pages}</span><button disabled={page === pages} onClick={() => setPage(value => value + 1)}>Next</button></nav>}
  </main>;
}
